import { CoreCheckbox, CoreInput, CoreSelect, MMButton } from '@components'
import CalendarIcon from '@images/icons/filter-calendar.svg?react'
import HintIcon from '@images/icons/hint_select_role_icon.svg?react'
import AddIcon from '@images/icons/new-icon.svg?react'
import { toastFailure, toastSuccess } from '@utils'
import ExpenseDragAndDrop from '@views/expenseManagement/@elements/ExpenseDragAndDrop'
import AccountingCategorySelect from '@withdrawals/components/AccountingCategorySelect'
import {
  useCreateReportMutation,
  useExpenseReportsOptionsQry,
  useMerchantQry,
  useUserProfileQry
} from 'mmfintech-backend-api'
import { checkFinalValue, tr, translateError } from 'mmfintech-commons'
import ReactTooltip from 'react-tooltip'
import UploadContainer from './UploadContainer'

const ExpenseDetailsTab = ({ formValues, fileObject, setFileObject, reportDisabled, currencyOptions }) => {
  const { profile } = useUserProfileQry()
  const { merchant } = useMerchantQry()
  const { reportsOptions, reportsFetching } = useExpenseReportsOptionsQry()

  const [createReportRequest, { isLoading: createReportLoading }] = useCreateReportMutation()

  const handleSelectFile = (file: File) => {
    setFileObject({
      fileUrl: URL.createObjectURL(file),
      type: file.type,
      fileName: file.name
    })
    formValues.setValue('file', file)
  }

  const handleRemoveFile = () => {
    setFileObject(null)
    formValues.setValue('file', null)
  }

  const handleCreateReport = async (name: string) => {
    const check = checkFinalValue(name, { validation: 'safe-string' })
    if (!check.valid) {
      toastFailure(translateError(check))
      return
    }

    try {
      const response = await createReportRequest({
        name,
        departmentId: profile?.departmentId,
        merchantName: merchant?.name
      }).unwrap()

      formValues.setValue('reportId', response?.id)
      toastSuccess(tr('FRONTEND.EXPENSE_MODAL.REPORT.CREATE_SUCCESS', 'Report created successfully'))
    } catch (err) {
      const error = err?.cause?.[0]?.cause || err.error
      if (error) {
        toastFailure(error)
      }
    }
  }

  return (
    <div className='expense-modal-body'>
      <div className='expense-modal-left-section'>
        <CoreSelect
          type='default'
          loading={reportsFetching}
          disabled={createReportLoading || reportDisabled}
          options={reportsOptions}
          enablePrefillOption={false}
          label={tr('FRONTEND.EXPENSE_MODAL.REPORT.LABEL', 'Report')}
          value={formValues.getValue('reportId')}
          error={formValues.getError('reportId')}
          required={true}
          onChange={(_, value) => formValues.setValue('reportId', value)}
          CreateComponent={({ filterString }: { filterString: string }) => {
            if (
              filterString &&
              !reportsOptions.some(({ label }) => label.toLowerCase() === filterString.toLowerCase())
            ) {
              return (
                <MMButton
                  loading={createReportLoading}
                  className='create-button'
                  variant='secondary'
                  onClick={() => handleCreateReport(filterString)}
                  caption={
                    <div className='create-content'>
                      <div>
                        <AddIcon /> {filterString}
                      </div>
                      <span className='create-label'>
                        {tr('FRONTEND.EXPENSE_MODAL.REPORT.CREATE_BUTTON', 'Create')}
                      </span>
                    </div>
                  }
                  size='normal'
                  fullWidth
                />
              )
            }
            return (
              <span className='create-content hint'>
                <AddIcon /> {tr('FRONTEND.EXPENSE_MODAL.REPORT.HINT', 'Start typing to search or create new')}
              </span>
            )
          }}
        />

        <div className='group-inputs'>
          <CoreSelect
            type='default'
            options={currencyOptions}
            label={tr('FRONTEND.EXPENSES.EXPENSE_MODAL.CURRENCY', 'Currency')}
            {...formValues.registerInput('currency')}
            data-test='expense-currency'
          />
          <CoreInput
            type='number'
            placeholder='0'
            label={tr('FRONTEND.EXPENSES.EXPENSE_MODAL.AMOUNT', 'Amount')}
            {...formValues.registerInput('amount')}
            data-test='expense-amount'
          />
        </div>

        <CoreCheckbox
          type='check'
          onClick={() => {
            const checked = !formValues.getValue('reimbursement')
            formValues.setValue('reimbursement', checked)
            formValues.setValue('counterpartyId', '')
            formValues.setValue('recipientId', '')
          }}
          checked={formValues.getValue('reimbursement')}
          value={'reimbursement'}
          label={tr('FRONTEND.EXPENSES.EXPENSE_MODAL.REIMBURSEMENT', 'Reimbursement')}
          className='reimbursement-checkbox'
          hint={
            <span
              style={{ marginLeft: '8px' }}
              data-for='reimbursement-tooltip'
              data-tip={tr(
                'FRONTEND.EXPENSE_MODAL.REIMBURSEMENT_HINT',
                'Reimbursement means you already paid the invoice and need to be reimbursed.<br>Only personal recipients will be shown or allowed to be created.'
              )}
            >
              <HintIcon width={16} height={16} />
            </span>
          }
        />
        <ReactTooltip id='reimbursement-tooltip' padding='8px 6px' html={true} aria-haspopup='true' />

        <div className='date-group-inputs'>
          <CoreInput
            type='date'
            name='dateOfPayment'
            label={tr('FRONTEND.EXPENSES.EXPENSE_MODAL.DATE', 'Invoice date')}
            {...formValues.registerInput('dateOfPayment')}
            placeholder={tr('FRONTEND.SECURITY.BUTTON_SELECT', 'Select')}
            LeftIcon={<CalendarIcon />}
            rangeDates
            endDate={formValues?.getValue('dueDate')}
            dataPickerForwardYears={5}
            dateFormat='dd/MM/yyyy'
            selectsStart
            popperPlacement='bottom-start'
            maxDate={formValues.getValue('dueDate')}
          />
          <CoreInput
            type='date'
            name='dueDate'
            {...formValues.registerInput('dueDate')}
            placeholder={tr('FRONTEND.SECURITY.BUTTON_SELECT', 'Select')}
            label={tr('FRONTEND.EXPENSES.EXPENSE_MODAL.DUE_DATE', 'Due date')}
            minDate={formValues.getValue('dateOfPayment')}
            LeftIcon={<CalendarIcon />}
            rangeDates
            startDate={formValues?.getValue('dateOfPayment')}
            dataPickerForwardYears={5}
            dateFormat='dd/MM/yyyy'
            selectsEnd
            popperPlacement='bottom-end'
          />
        </div>

        <CoreInput
          type='text'
          {...formValues.registerInput('invoiceNumber')}
          label={tr('FRONTEND.EXPENSES.EXPENSE_MODAL.INVOICE_NUMBER', 'Invoice number')}
        />
        <CoreInput
          type='text'
          {...formValues.registerInput('category')}
          label={tr('FRONTEND.EXPENSES.EXPENSE_MODAL.CATEGORY', 'Category')}
        />

        <AccountingCategorySelect formValues={formValues} />
      </div>

      <div className='expense-modal-right-section'>
        <ExpenseDragAndDrop
          file={fileObject}
          component={UploadContainer}
          onSelect={handleSelectFile}
          onRemove={handleRemoveFile}
          error={formValues.getError('file')}
        />
      </div>
    </div>
  )
}

export default ExpenseDetailsTab
